import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import api from "utils/api";
import Calendar, { toISODate } from "components/Calendar";
import { formatPrice } from "constants/massages";
import { useTeamLocale } from "./TeamLocaleContext";
import {
  WAKEUP_STUDIO_OPTION,
  CLASSIC_STUDIO_OPTION,
  PREMIUM_STUDIO_OPTION,
  BUSINESS_OPTIONS,
  LEGACY_OPTIONS,
  isDeliveryChoiceOption,
  optionForDelivery,
  availabilityServiceKeyForOption,
  wakeupServiceDurationMinutes,
  isWakeupBookingOption,
  isWakeupMobileOption,
  isWakeupPlusOption,
  usesDailyHoffmannSchedule,
  isThursdayOnlyStudio,
  isLicensePartnerOption,
  isStarterPackageOption,
  skipsTimeslotSelection,
  buildServiceAddressForOption,
  buildLocalPricePreview,
  studioLocationForOption,
  studioScheduleForOption,
  filterSlotsForOption,
  bookingHoursHintForOption,
  slotStepMinutesForOption,
  arrangedAppointmentStartISO,
  licensePartnerMinDate,
  isAfterHoffmannOption1Cutoff,
  formatTime,
  hasAdjustableTravelFee,
} from "scenes/booking";

// Same catalog as /buchen — adding/editing an option there flows through here.
// Delivery-choice services (Studio vs. Vor Ort) appear once; the location is
// picked via a second dropdown that only shows for those services.
const OPTION_ENTRIES = [
  { key: "wakeup", option: WAKEUP_STUDIO_OPTION },
  { key: "classic", option: CLASSIC_STUDIO_OPTION },
  { key: "premium", option: PREMIUM_STUDIO_OPTION },
  ...BUSINESS_OPTIONS.map((o) => ({ key: o.id, option: o })),
  ...LEGACY_OPTIONS.map((o) => ({ key: o.id, option: o })),
].filter((entry) => entry.option);

const labelForEntry = (entry) => entry.option.title;

const isFreeConsultationOption = (option) =>
  option?.bookingMode === "consultation" && !option?.paidPackage;

const ManualBookingSuccessDialog = ({ open, details, onClose, t }) => {
  const closeButtonRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    closeButtonRef.current?.focus();
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  return createPortal(
    <div className="team-manual-success-root">
      <div
        className="team-manual-success-overlay"
        onClick={onClose}
        aria-hidden="true"
      />
      <div
        className="team-manual-success-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="team-manual-success-title"
        aria-describedby="team-manual-success-desc"
      >
        <span className="team-manual-success-mark" aria-hidden="true" />
        <p className="team-manual-success-kicker">{t("manual.successKicker")}</p>
        <h2 id="team-manual-success-title">{t("manual.successTitle")}</h2>
        <p id="team-manual-success-desc" className="team-manual-success-lead">
          {t("manual.successLead", { email: details?.email })}
        </p>
        <dl className="team-manual-success-details">
          <div>
            <dt>{t("manual.successService")}</dt>
            <dd>{details?.service || t("calendar.bookingDetail.none")}</dd>
          </div>
          <div>
            <dt>{t("manual.successNotification")}</dt>
            <dd>
              {details?.notifyCustomer
                ? t("manual.successNotificationYes")
                : t("manual.successNotificationNo")}
            </dd>
          </div>
        </dl>
        <button
          type="button"
          className="auth-submit team-manual-success-close"
          onClick={onClose}
          ref={closeButtonRef}
        >
          {t("manual.successClose")}
        </button>
      </div>
    </div>,
    document.body
  );
};

const ManualBookingForm = ({ onCreated }) => {
  const { t } = useTeamLocale();

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");

  const [optionKey, setOptionKey] = useState("");
  const [comesToYou, setComesToYou] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [slots, setSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [personCount, setPersonCount] = useState(1);
  const [address, setAddress] = useState({ companyName: "", fullAddress: "", notes: "" });
  const [hasVoucher, setHasVoucher] = useState(false);
  const [voucherTermsAccepted, setVoucherTermsAccepted] = useState(false);
  const [applyTravelFee, setApplyTravelFee] = useState(true);
  const [useCustomPrice, setUseCustomPrice] = useState(false);
  const [customPriceEuro, setCustomPriceEuro] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("on-site");
  const [notes, setNotes] = useState("");
  const [notifyCustomer, setNotifyCustomer] = useState(true);

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const baseEntry = OPTION_ENTRIES.find((e) => e.key === optionKey) || null;
  const baseOption = baseEntry?.option || null;
  const isDeliveryChoice = isDeliveryChoiceOption(baseOption);
  const option = isDeliveryChoice
    ? optionForDelivery(baseOption, comesToYou)
    : baseOption;

  const appointmentIsArranged = skipsTimeslotSelection(option);
  const requiresAddress = Boolean(option?.requiresAddress);
  const requiresVoucherQuestion = Boolean(option?.requiresVoucherQuestion);
  const isFreeConsultation = isFreeConsultationOption(option);
  const showTravelFeeToggle = hasAdjustableTravelFee(option) && !hasVoucher;

  const personCountChoices = useMemo(() => {
    if (!option?.personCountMax) return [];
    const min = Number(option.personCountMin || 1);
    const max = Number(option.personCountMax || min);
    return Array.from({ length: Math.max(0, max - min + 1) }, (_, idx) => min + idx);
  }, [option]);
  const showPersonCount = personCountChoices.length > 0;
  const selectedPersonCount = Number(personCount || option?.personCountDefault || 1);
  const personCountLabel = isWakeupPlusOption(option) ? "Mitarbeitende" : "Personen";

  const minDate = isLicensePartnerOption(option) ? licensePartnerMinDate() : new Date();
  const maxDate = new Date(Date.now() + 60 * 24 * 60 * 60 * 1000);

  const pricePreview = useMemo(
    () =>
      buildLocalPricePreview(
        option,
        selectedPersonCount,
        hasVoucher,
        applyTravelFee,
        "stripe"
      ),
    [option, selectedPersonCount, hasVoucher, applyTravelFee]
  );
  const displayPriceCents = isFreeConsultation
    ? 0
    : pricePreview?.grossCents ?? (option?.priceCents ?? null);

  const parseEuroToCents = (raw) => {
    const value = String(raw || "").trim().replace(",", ".");
    if (!value) return null;
    const euros = Number(value);
    if (!Number.isFinite(euros) || euros < 0) return null;
    return Math.round(euros * 100);
  };

  const customPriceCents = useCustomPrice ? parseEuroToCents(customPriceEuro) : null;
  const effectivePriceCents =
    useCustomPrice && customPriceCents != null ? customPriceCents : displayPriceCents;

  const resetBookingFields = useCallback(() => {
    setSelectedDate(null);
    setSlots([]);
    setSelectedSlot(null);
    setPersonCount(1);
    setAddress({ companyName: "", fullAddress: "", notes: "" });
    setHasVoucher(false);
    setVoucherTermsAccepted(false);
    setApplyTravelFee(true);
    setUseCustomPrice(false);
    setCustomPriceEuro("");
    setNotes("");
  }, []);

  const handleOptionChange = (key) => {
    setOptionKey(key);
    setComesToYou(false);
    const nextOption = OPTION_ENTRIES.find((e) => e.key === key)?.option || null;
    setSelectedDate(null);
    setSlots([]);
    setSelectedSlot(null);
    setPersonCount(Number(nextOption?.personCountDefault || 1));
    setAddress({ companyName: "", fullAddress: "", notes: "" });
    setHasVoucher(false);
    setVoucherTermsAccepted(false);
    setApplyTravelFee(true);
    setUseCustomPrice(false);
    setCustomPriceEuro("");
    setPaymentMethod(isFreeConsultationOption(nextOption) ? "later" : "on-site");
    setError(null);
  };

  const handleDeliveryChange = (nextComesToYou) => {
    setComesToYou(nextComesToYou);
    setSelectedSlot(null);
    setHasVoucher(false);
    setVoucherTermsAccepted(false);
    setApplyTravelFee(true);
    setUseCustomPrice(false);
    setCustomPriceEuro("");
    setAddress({ companyName: "", fullAddress: "", notes: "" });
  };

  const handleCustomPriceToggle = (checked) => {
    setUseCustomPrice(checked);
    if (checked && displayPriceCents != null) {
      setCustomPriceEuro(String((displayPriceCents / 100).toFixed(2)).replace(".", ","));
    } else {
      setCustomPriceEuro("");
    }
  };

  useEffect(() => {
    if (!option || appointmentIsArranged || !selectedDate) {
      setSlots([]);
      setSelectedSlot(null);
      return undefined;
    }
    let cancelled = false;
    setLoadingSlots(true);
    setSelectedSlot(null);
    const availabilityDuration = isWakeupBookingOption(option)
      ? wakeupServiceDurationMinutes(option, selectedPersonCount)
      : option.durationMinutes;
    const params = {
      date: toISODate(selectedDate),
      durationMinutes: availabilityDuration,
      brand: "dr-hoffmann",
      serviceKey: availabilityServiceKeyForOption(option),
    };
    const slotStepMinutes = slotStepMinutesForOption(option);
    if (slotStepMinutes) params.slotStepMinutes = slotStepMinutes;
    if (isWakeupBookingOption(option)) params.personCount = selectedPersonCount;
    if (option.hoffmannBookingOption) {
      params.hoffmannBookingOption = option.hoffmannBookingOption;
    }
    if (option.bookingMode === "studio-weekday" && !usesDailyHoffmannSchedule(option)) {
      params.studioSchedule = "weekday";
      params.location = studioLocationForOption(option, selectedDate);
    } else if (option.bookingMode === "studio-weekday") {
      params.studioSchedule = studioScheduleForOption(option, selectedDate);
      params.location = studioLocationForOption(option, selectedDate);
    }
    api
      .get("/bookings/availability", { params })
      .then(({ data }) => {
        if (!cancelled) {
          setSlots(filterSlotsForOption(option, selectedDate, data?.slots));
        }
      })
      .catch(() => {
        if (!cancelled) setSlots([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingSlots(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [optionKey, comesToYou, appointmentIsArranged, selectedDate, selectedPersonCount]);

  const updateAddress = (key) => (e) =>
    setAddress((prev) => ({ ...prev, [key]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    if (!firstName.trim() || !lastName.trim() || !email.trim()) {
      return setError("Bitte Vorname, Nachname und E-Mail des Kunden angeben.");
    }
    if (!option) return setError("Bitte eine Leistung auswählen.");
    if (!appointmentIsArranged && !selectedSlot) {
      return setError("Bitte einen Termin auswählen.");
    }
    if (requiresAddress && !address.fullAddress.trim()) {
      return setError("Bitte die Adresse für die Vor-Ort-Massage angeben.");
    }
    if (requiresVoucherQuestion && hasVoucher && !voucherTermsAccepted) {
      return setError("Bitte bestätigen, dass der Gutschein zum Termin mitgebracht wird.");
    }
    if (useCustomPrice) {
      const cents = parseEuroToCents(customPriceEuro);
      if (cents == null) {
        return setError(t("manual.customPriceInvalid"));
      }
      if (cents > 100_000) {
        return setError(t("manual.customPriceTooHigh"));
      }
    }

    setSubmitting(true);
    try {
      const body = {
        customer: {
          firstName: firstName.trim(),
          lastName: lastName.trim(),
          email: email.trim(),
          phone: phone.trim(),
        },
        serviceKey: option.serviceKey,
        durationMinutes: option.durationMinutes,
        paymentMethod: isFreeConsultation ? "later" : paymentMethod,
        notifyCustomer,
      };
      if (option.offerType) body.offerType = option.offerType;
      if (showPersonCount || option.personCountDefault) {
        body.personCount = selectedPersonCount;
      }
      if (isWakeupMobileOption(option)) {
        body.serviceDurationMinutes = wakeupServiceDurationMinutes(
          option,
          selectedPersonCount
        );
      }
      if (requiresVoucherQuestion) {
        body.hasVoucher = hasVoucher;
        body.voucherTermsAccepted = hasVoucher && voucherTermsAccepted;
      }
      if (showTravelFeeToggle) {
        body.applyTravelFee = applyTravelFee;
      }
      if (selectedSlot) {
        body.startISO = selectedSlot;
      } else if (appointmentIsArranged) {
        body.startISO = arrangedAppointmentStartISO();
      }
      if (isStarterPackageOption(option)) {
        body.hoffmannBookingOption = option.hoffmannBookingOption;
        if (option.priceCents && !useCustomPrice) body.priceCents = option.priceCents;
      } else if (option.hoffmannBookingOption) {
        body.hoffmannBookingOption = option.hoffmannBookingOption;
      } else {
        body.hoffmannBookingOption = option.title;
      }
      if (option.bookingMode === "studio-weekday") {
        const studioSchedule = studioScheduleForOption(option, selectedDate);
        body.location = studioLocationForOption(option, selectedDate);
        body.studioSchedule = studioSchedule;
        body.studioScheduleWeekday =
          !usesDailyHoffmannSchedule(option) ||
          studioSchedule === "thursday";
      }
      if (requiresAddress) {
        body.serviceAddress = buildServiceAddressForOption(option, {
          companyName: address.companyName,
          fullAddress: address.fullAddress,
          notes: address.notes,
        });
        if (address.fullAddress) body.address = address.fullAddress;
      }
      if (notes.trim()) body.notes = notes.trim();
      if (useCustomPrice) {
        body.useCustomPrice = true;
        body.amountEuro = customPriceEuro.trim();
      }

      const { data } = await api.post("/admin/bookings", body);
      setSuccess({
        email: data?.booking?.customer?.email || email.trim(),
        service: option.title,
        notifyCustomer,
      });
      setFirstName("");
      setLastName("");
      setEmail("");
      setPhone("");
      setOptionKey("");
      setComesToYou(false);
      setApplyTravelFee(true);
      setUseCustomPrice(false);
      setCustomPriceEuro("");
      setPaymentMethod("on-site");
      setNotifyCustomer(true);
      resetBookingFields();
      onCreated?.();
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          "Die Buchung konnte nicht angelegt werden."
      );
    } finally {
      setSubmitting(false);
    }
    return undefined;
  };

  return (
    <form className="team-manual-form" onSubmit={handleSubmit}>
      <ManualBookingSuccessDialog
        open={Boolean(success)}
        details={success}
        onClose={() => setSuccess(null)}
        t={t}
      />
      <p className="team-lead">{t("manual.formLead")}</p>

      {error && <div className="auth-alert is-error">{error}</div>}

      <div className="booking-step">
        <div className="step-head">
          <h2>
            {t("manual.customerTitle")}
          </h2>
          <span className="booking-step-number">01</span>
        </div>
        <div className="address-grid">
          <div>
            <label htmlFor="mb-first">{t("manual.firstName")} *</label>
            <input
              id="mb-first"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
            />
          </div>
          <div>
            <label htmlFor="mb-last">{t("manual.lastName")} *</label>
            <input
              id="mb-last"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
            />
          </div>
          <div>
            <label htmlFor="mb-email">{t("manual.email")} *</label>
            <input
              id="mb-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
          <div>
            <label htmlFor="mb-phone">{t("manual.phone")}</label>
            <input
              id="mb-phone"
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="+49 ..."
            />
          </div>
        </div>
      </div>

      <div className="booking-step">
        <div className="step-head">
          <h2>{t("manual.serviceTitle")}</h2>
          <span className="booking-step-number">02</span>
        </div>
        <div className="address-grid">
          <div className="full">
            <label htmlFor="mb-service">{t("manual.service")} *</label>
            <select
              id="mb-service"
              value={optionKey}
              onChange={(e) => handleOptionChange(e.target.value)}
            >
              <option value="">{t("manual.servicePlaceholder")}</option>
              {OPTION_ENTRIES.map((e) => (
                <option key={e.key} value={e.key}>
                  {labelForEntry(e)}
                </option>
              ))}
            </select>
          </div>
          {isDeliveryChoice && (
            <div className="full">
              <label htmlFor="mb-delivery">{t("manual.deliveryLabel")} *</label>
              <select
                id="mb-delivery"
                value={comesToYou ? "onsite" : "studio"}
                onChange={(e) => handleDeliveryChange(e.target.value === "onsite")}
              >
                <option value="studio">{t("manual.deliveryStudio")}</option>
                <option value="onsite">{t("manual.deliveryOnsite")}</option>
              </select>
            </div>
          )}
          {showTravelFeeToggle && (
            <div className="full">
              <label className="team-checkbox">
                <input
                  type="checkbox"
                  checked={applyTravelFee}
                  onChange={(e) => setApplyTravelFee(e.target.checked)}
                />
                {t("manual.applyTravelFee")}
              </label>
            </div>
          )}
          {option && !isFreeConsultation && displayPriceCents != null && (
            <div className="full">
              <label className="team-checkbox">
                <input
                  type="checkbox"
                  checked={useCustomPrice}
                  onChange={(e) => handleCustomPriceToggle(e.target.checked)}
                />
                {t("manual.useCustomPrice")}
              </label>
            </div>
          )}
          {option && useCustomPrice && (
            <div className="full">
              <label htmlFor="mb-custom-price">{t("manual.customPrice")} *</label>
              <input
                id="mb-custom-price"
                type="text"
                inputMode="decimal"
                placeholder="0,00"
                value={customPriceEuro}
                onChange={(e) => setCustomPriceEuro(e.target.value)}
                required
              />
            </div>
          )}
          {option && effectivePriceCents != null && (
            <div className="full">
              <p className="field-note">
                {t("manual.priceLabel")}:{" "}
                <strong>
                  {effectivePriceCents === 0
                    ? t("manual.free")
                    : formatPrice(effectivePriceCents)}
                </strong>
                {useCustomPrice && (
                  <span className="team-muted"> ({t("manual.customPriceActive")})</span>
                )}
              </p>
            </div>
          )}
          {showPersonCount && (
            <div className="full">
              <label htmlFor="mb-person">{personCountLabel} *</label>
              <select
                id="mb-person"
                value={selectedPersonCount}
                onChange={(e) => setPersonCount(Number(e.target.value))}
              >
                {personCountChoices.map((count) => (
                  <option key={count} value={count}>
                    {count}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>
      </div>

      {option && (
        <div className="booking-step">
          <div className="step-head">
            <h2>{t("manual.dateTitle")}</h2>
            <span className="booking-step-number">03</span>
          </div>
          {appointmentIsArranged ? (
            <p className="step-summary">{t("manual.arranged")}</p>
          ) : (
            <div className="team-manual-scheduler">
              <Calendar
                selectedDate={selectedDate}
                onSelect={setSelectedDate}
                minDate={minDate}
                maxDate={maxDate}
                weekdaysOnly={
                  !isThursdayOnlyStudio(option) && !usesDailyHoffmannSchedule(option)
                }
                onlyWeekday={isThursdayOnlyStudio(option) ? 4 : -1}
                closedWeekday={-1}
                disableDate={
                  isThursdayOnlyStudio(option) ? isAfterHoffmannOption1Cutoff : undefined
                }
              />
              {selectedDate && (
                <>
                  {bookingHoursHintForOption(option, selectedDate) && (
                    <p className="step-summary" style={{ marginTop: "1rem" }}>
                      {bookingHoursHintForOption(option, selectedDate)}
                    </p>
                  )}
                  {loadingSlots ? (
                    <p className="step-summary" style={{ marginTop: "1rem" }}>
                      {t("manual.slotsLoading")}
                    </p>
                  ) : slots.length === 0 ? (
                    <p className="step-summary" style={{ marginTop: "1rem" }}>
                      {t("manual.slotsEmpty")}
                    </p>
                  ) : (
                    <div className="slot-grid">
                      {slots.map((iso) => (
                        <button
                          type="button"
                          key={iso}
                          onClick={() => setSelectedSlot(iso)}
                          className={`slot-button ${selectedSlot === iso ? "is-selected" : ""}`}
                        >
                          {formatTime(iso)}
                        </button>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      )}

      {option && requiresAddress && (
        <div className="booking-step">
          <div className="step-head">
            <h2>{t("manual.addressTitle")}</h2>
            <span className="booking-step-number">04</span>
          </div>
          <div className="address-grid">
            <div className="full">
              <label htmlFor="mb-company">{t("manual.companyName")}</label>
              <input
                id="mb-company"
                value={address.companyName}
                onChange={updateAddress("companyName")}
              />
            </div>
            <div className="full">
              <label htmlFor="mb-address">{t("manual.address")} *</label>
              <input
                id="mb-address"
                value={address.fullAddress}
                onChange={updateAddress("fullAddress")}
                placeholder="Straße Hausnummer, PLZ Ort"
              />
            </div>
            <div className="full">
              <label htmlFor="mb-address-notes">{t("manual.addressNotes")}</label>
              <textarea
                id="mb-address-notes"
                rows={2}
                maxLength={300}
                value={address.notes}
                onChange={updateAddress("notes")}
                placeholder="Stockwerk, Klingelschild …"
              />
            </div>
          </div>
        </div>
      )}

      {option && requiresVoucherQuestion && (
        <div className="booking-step">
          <div className="step-head">
            <h2>{t("manual.voucherTitle")}</h2>
            <span className="booking-step-number">05</span>
          </div>
          <label className="team-checkbox">
            <input
              type="checkbox"
              checked={hasVoucher}
              onChange={(e) => {
                setHasVoucher(e.target.checked);
                setVoucherTermsAccepted(false);
                if (e.target.checked) setApplyTravelFee(true);
              }}
            />
            {t("manual.hasVoucher")}
          </label>
          {hasVoucher && (
            <label className="team-checkbox">
              <input
                type="checkbox"
                checked={voucherTermsAccepted}
                onChange={(e) => setVoucherTermsAccepted(e.target.checked)}
              />
              {t("manual.voucherTerms")}
            </label>
          )}
        </div>
      )}

      {option && (
        <div className="booking-step">
          <div className="step-head">
            <h2>{t("manual.detailsTitle")}</h2>
            <span className="booking-step-number">06</span>
          </div>
          <div className="address-grid">
            {!isFreeConsultation && (
              <div className="full">
                <label htmlFor="mb-payment">{t("manual.payment")}</label>
                <select
                  id="mb-payment"
                  value={paymentMethod}
                  onChange={(e) => setPaymentMethod(e.target.value)}
                >
                  <option value="on-site">{t("manual.paymentOnSite")}</option>
                  <option value="later">{t("manual.paymentLater")}</option>
                </select>
              </div>
            )}
            <div className="full">
              <label htmlFor="mb-notes">{t("manual.notes")}</label>
              <textarea
                id="mb-notes"
                rows={3}
                maxLength={500}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder={t("manual.notesPlaceholder")}
              />
            </div>
            <div className="full">
              <label className="team-checkbox">
                <input
                  type="checkbox"
                  checked={notifyCustomer}
                  onChange={(e) => setNotifyCustomer(e.target.checked)}
                />
                {t("manual.notifyCustomer")}
              </label>
            </div>
          </div>
        </div>
      )}

      <div className="team-manual-form-actions">
        <button type="submit" className="auth-submit" disabled={submitting}>
          {submitting ? t("manual.submitting") : t("manual.submit")}
        </button>
      </div>
    </form>
  );
};

export default ManualBookingForm;
